import { useCallback, useRef, useState } from 'react';

import { API, ButtonType } from '../../types/enums';
import Button from '../Button/Button';
import DeleteIcon from '../DeleteIcon/DeleteIcon';

import { ReactComponent as PlaceholderIcon } from './image-solid.svg';

import styles from './ImageInput.module.scss';
import baseStyles from './Input.module.scss';

interface ImageInputProps {
  id: string;
  label: string;
  value?: string;
  helperText?: string;
  setValue: (val: string) => void;
}

const ImageInput = ({ id, value = '', label, setValue, helperText = '' }: ImageInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>();
  const [currentSrc, setCurrentSrc] = useState(
    value ? process.env.REACT_APP_ASSETS_CDN + value : value
  );
  const [error, setError] = useState('');

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setError('');

      if (file.size > 500 * 1024) {
        setError('Max file size: 500kb');
      } else {
        setCurrentSrc(URL.createObjectURL(file));

        try {
          const result = await toBase64(file);
          setValue(result?.toString() ?? '');
        } catch (error) {
          setError('Image processing failed, please select another image');
          return;
        }
      }
    }
  }, []);

  const onFileSelectClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={baseStyles.label}>{label}</div>
      <div className={styles.input}>
        <label htmlFor={id}>
          <Button action={onFileSelectClick} small spaceSaving type={ButtonType.SECONDARY}>
            Upload icon
          </Button>
          <input
            ref={(el) => (inputRef.current = el)}
            onChange={handleFileChange}
            {...{ id }}
            name={id}
            type="file"
            accept="image/*"
            hidden
          />
        </label>
        {currentSrc ? (
          <div className={styles.icon}>
            <img src={currentSrc} />
            <DeleteIcon
              onDelete={() => {
                setCurrentSrc('');
                setValue('');
              }}
            />
          </div>
        ) : (
          <PlaceholderIcon />
        )}
      </div>
      {helperText && <div className={styles.helperText}>{helperText}</div>}
      {error && <div className={baseStyles.errorText}>{error}</div>}
    </div>
  );
};

const toBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export default ImageInput;
